import { useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { PaywallDialog } from "@/components/paywall-dialog"
import { StatusPill } from "@/components/status-pill"
import { formatEventDateTime } from "@/lib/dates"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Chip } from "@/components/chips"
import { EmptyState } from "@/components/empty-state"
import { EntityRow } from "@/components/entity-row"
import { ListPageShell } from "@/components/list-page-shell"
import { PageHeader } from "@/components/page-header"
import { useCompanyCapabilities } from "@/lib/company-capabilities"

export const Route = createFileRoute("/recruit/hackathons")({
  component: EmployerHackathons,
})

type StatusFilter = "active" | "published" | "draft" | "cancelled" | "all"
type SortOrder = "newest" | "most_registrations" | "soonest"

function registrationLabel(count: number) {
  if (count === 0) return "No registrations"
  if (count === 1) return "1 registration"
  return `${count} registrations`
}

function safeText(value: string | undefined | null) {
  return (value ?? "").trim()
}

function matchesHackathonSearch(
  h: {
    title?: string
    venue?: string
    city?: string
    address?: string
  },
  q: string,
) {
  if (!q) return true
  return (
    safeText(h.title).toLowerCase().includes(q) ||
    safeText(h.venue).toLowerCase().includes(q) ||
    safeText(h.city).toLowerCase().includes(q) ||
    safeText(h.address).toLowerCase().includes(q)
  )
}

function EmployerHackathons() {
  const capabilities = useCompanyCapabilities()
  const canManageHackathons = capabilities?.manageHackathons === true
  const hackathons = useQuery(
    api.hackathons.listMine,
    canManageHackathons ? {} : "skip",
  )
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState<StatusFilter>("active")
  const [city, setCity] = useState("all")
  const [sort, setSort] = useState<SortOrder>("newest")
  const [payId, setPayId] = useState<Id<"hackathons"> | null>(null)

  const cities = useMemo(() => {
    if (!hackathons) return []
    return Array.from(
      new Set(hackathons.map((h) => h.city).filter((c): c is string => Boolean(c))),
    ).sort()
  }, [hackathons])

  const filteredHackathons = useMemo(() => {
    if (!hackathons) return []
    const q = safeText(search).toLowerCase()
    const filtered = hackathons.filter((h) => {
      if (status === "active" && h.status === "cancelled") return false
      if (status !== "all" && status !== "active" && h.status !== status) {
        return false
      }
      if (city !== "all" && h.city !== city) return false
      return matchesHackathonSearch(h, q)
    })

    return [...filtered].sort((a, b) => {
      const aRegs = a.registrationCount ?? 0
      const bRegs = b.registrationCount ?? 0
      if (sort === "most_registrations") {
        const diff = bRegs - aRegs
        return diff !== 0 ? diff : b._creationTime - a._creationTime
      }
      if (sort === "soonest") {
        return a.startsAt - b.startsAt
      }
      return b._creationTime - a._creationTime
    })
  }, [hackathons, search, status, city, sort])

  const clearFilters = () => {
    setSearch("")
    setStatus("active")
    setCity("all")
    setSort("newest")
  }

  const hasFilters =
    safeText(search) !== "" ||
    status !== "active" ||
    city !== "all" ||
    sort !== "newest"

  const publishedCount =
    hackathons?.filter((h) => h.status === "published").length ?? 0
  const draftCount = hackathons?.filter((h) => h.status === "draft").length ?? 0

  if (capabilities !== undefined && !canManageHackathons) {
    return (
      <div className="mkt-panel mkt-panel--wash mx-auto max-w-xl">
        <p className="mkt-panel__title">Manager access required</p>
        <p className="mkt-panel__copy">
          Company events and checkout are managed by an owner or admin.
        </p>
        <Link to="/recruit/jobs" className="home-wf-btn home-wf-btn--outline mt-4">
          Back to recruiting
        </Link>
      </div>
    )
  }

  return (
    <div>
      <ListPageShell
        header={
          <PageHeader
            title="Your hackathons"
            subtitle={
              hackathons === undefined
                ? "Loading your events…"
                : hackathons.length === 0
                  ? "Drafts are free — pay ₹3,000 only when you publish."
                  : `${hackathons.length} event${hackathons.length === 1 ? "" : "s"} · ${publishedCount} live · ${draftCount} draft${draftCount === 1 ? "" : "s"} · tap an event to manage registrations`
            }
            actions={
              <Link
                to="/recruit/hackathons/new"
                className="home-wf-btn home-wf-btn--lime shrink-0"
              >
                New hackathon
              </Link>
            }
          />
        }
        isLoading={hackathons === undefined}
        emptyState={
          hackathons?.length === 0 ? (
            <EmptyState
              className="mt-8"
              title="You haven't created any hackathons yet."
              action={{
                label: "Create your first hackathon",
                to: "/recruit/hackathons/new",
                emphasis: true,
              }}
            />
          ) : null
        }
        filters={
          <div className="mkt-filters">
            <Input
              placeholder="Search title, venue, city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as StatusFilter)}
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="published">Live</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="all">All status</SelectItem>
              </SelectContent>
            </Select>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cities</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={sort}
              onValueChange={(value) => setSort(value as SortOrder)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="most_registrations">
                  Most registrations
                </SelectItem>
                <SelectItem value="soonest">Soonest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        countLine={
          <p className="mt-4 text-sm text-[color:var(--wf-mute)]">
            {filteredHackathons.length === 0
              ? "No hackathons match your filters"
              : `${filteredHackathons.length} event${filteredHackathons.length === 1 ? "" : "s"}${
                  status === "published"
                    ? " live"
                    : status === "draft"
                      ? " in draft"
                      : status === "cancelled"
                        ? " cancelled"
                        : ""
                }`}
          </p>
        }
        noMatches={
          filteredHackathons.length === 0 ? (
            <EmptyState
              className="mt-2"
              title="No hackathons match your filters."
              action={
                hasFilters
                  ? { label: "Clear filters", onClick: clearFilters }
                  : undefined
              }
            />
          ) : null
        }
      >
        <div className="mt-2">
          {filteredHackathons.map((h) => {
            const payPending =
              h.status === "draft" && h.paymentStatus === "pending"
            return (
              <EntityRow
                key={h._id}
                to="/recruit/hackathons/$hackathonId"
                params={{ hackathonId: h._id }}
                search={{ pay: false }}
                title={h.title}
                chips={
                  <>
                    <StatusPill domain="hackathon" status={h.status} />
                    <Chip emphasis={(h.registrationCount ?? 0) > 0}>
                      {registrationLabel(h.registrationCount ?? 0)}
                    </Chip>
                    {payPending && <Chip>Pay to publish</Chip>}
                    {h.capacity != null && <Chip>{h.capacity} seats</Chip>}
                  </>
                }
                meta={
                  <>
                    {[h.venue, h.city].filter(Boolean).join(" · ")}
                    {(h.venue || h.city) && " · "}
                    {formatEventDateTime(h.startsAt)}
                  </>
                }
                cue="Manage →"
                action={
                  payPending ? (
                    <button
                      type="button"
                      className="home-wf-btn home-wf-btn--lime mkt-page-row__pay"
                      onClick={() => setPayId(h._id)}
                    >
                      Pay ₹3,000
                    </button>
                  ) : undefined
                }
              />
            )
          })}
        </div>
      </ListPageShell>

      <PaywallDialog
        open={!!payId}
        onOpenChange={(open) => !open && setPayId(null)}
        kind="employer_hackathon"
        hackathonId={payId ?? undefined}
        onPaid={() => setPayId(null)}
      />
    </div>
  )
}
